import { FormProvider, SubmitHandler, UseFormReturn, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js';
import * as yup from 'yup';
import { MouseEvent, ReactNode, Ref, useEffect, useMemo } from 'react';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import React from 'react';

import { DescribeStep } from './steps/describe-step';
import { FeelingStep } from './steps/feeling-step';
import { ConnectAgainStep } from './steps/connect-again-step';
import { FeedbackStep } from './steps/feedback-step';
import { ThankYouStep } from './steps/thank-you-step';
import { PostCall, PostCallData, useUpdatePostCall } from '../../hooks/api/usePostCall';
import Toast from '../common/pop-up-message';

export interface PostCallFormRef extends UseFormReturn {
  step: number;
  setStep: (step: number) => void;
}

export interface PostCallFormProps {
  setStep: React.Dispatch<React.SetStateAction<PostCallFormSteps>>;
  step: PostCallFormSteps;
  postCall?: PostCall;
  onCancel?: () => void;
  className?: string;
  formRef?: Ref<PostCallFormRef>;
}

export enum PostCallFormSteps {
  DESCRIBE,
  FEELING,
  CONNECT_AGAIN,
  FEEDBACK,
  THANK_YOU,
}

const validationSchema = {
  [PostCallFormSteps.DESCRIBE]: yup.object({
    description: yup.string().notRequired(),
  }),
  [PostCallFormSteps.FEELING]: yup.object({
    moodScore: yup.number().min(1).max(10).notRequired(),
  }),
  [PostCallFormSteps.CONNECT_AGAIN]: yup.object({
    connectAgain: yup.boolean().notRequired(),
  }),
  [PostCallFormSteps.FEEDBACK]: yup.object({
    feedback: yup.string().notRequired(),
  }),
  [PostCallFormSteps.THANK_YOU]: yup.object(),
};

export const PostCallForm: React.FC<PostCallFormProps> = ({
  step,
  setStep,
  postCall,
  onCancel,
  className,
  formRef,
}) => {
  const postCallMutation = useUpdatePostCall();

  const methods = useForm({
    defaultValues: JSON.parse(sessionStorage.getItem(`postCallForm-${postCall?.id}`) || 'null') || {
      moodScore: null as number | null,
    },
    resolver: yupResolver(validationSchema[step]),
    mode: 'onChange',
  });

  const { handleSubmit, watch } = methods;

  const submit: SubmitHandler<PostCallData> = async (data) => {
    if (postCall) {
      postCallMutation.mutate(
        { postCallId: postCall.id, payload: data },
        {
          onSuccess: () => {
            setStep(PostCallFormSteps.THANK_YOU);
          },
          onError: () => {
            Toast.error('Something went wrong. Please try again.');
          },
        },
      );
    }
  };

  const goToNextStep = async (event: MouseEvent) => {
    event.preventDefault();

    if (step === PostCallFormSteps.FEELING) {
      const moodScore = watch('moodScore');
      if (!moodScore) {
        Toast.error('Please select how you are feeling.');
        return;
      }
    }

    setStep(step + 1);
  };

  const currentRenderStep = useMemo(() => {
    switch (step) {
      case PostCallFormSteps.DESCRIBE:
        return <DescribeStep postCall={postCall} />;
      case PostCallFormSteps.FEELING:
        return <FeelingStep postCall={postCall} />;
      case PostCallFormSteps.CONNECT_AGAIN:
        return <ConnectAgainStep postCall={postCall} />;
      case PostCallFormSteps.FEEDBACK:
        return <FeedbackStep postCall={postCall} />;
      case PostCallFormSteps.THANK_YOU:
        return <ThankYouStep onClose={onCancel} />;
      default:
        return null;
    }
  }, [postCall, step, onCancel]);

  useEffect(() => {
    sessionStorage.removeItem(`postCallForm-${postCall?.id}`);
  }, [postCall]);

  return (
    <FormProvider {...methods}>
      <form
        ref={() => {
          if (formRef) {
            const currentFormRef = {
              step,
              setStep,
              ...methods,
            };

            if (typeof formRef === 'function') {
              formRef(currentFormRef);
            } else {
              // @ts-ignore
              formRef.current = currentFormRef;
            }
          }
        }}
        onSubmit={handleSubmit(submit)}
        className={twMerge('flex flex-col', className)}
      >
        <div className="flex-1 px-6 py-6">{currentRenderStep}</div>
        {step <= PostCallFormSteps.FEEDBACK && (
          <ButtonPanel
            onNextStep={step <= PostCallFormSteps.CONNECT_AGAIN ? goToNextStep : undefined}
            onCancel={onCancel}
            type={step <= PostCallFormSteps.CONNECT_AGAIN ? 'button' : 'submit'}
            isLoading={postCallMutation.isLoading}
          />
        )}
        {step === PostCallFormSteps.THANK_YOU && onCancel && (
          <div className="border-input-border-gray flex justify-end border-t px-6 py-4">
            <button
              className="bg-button-primary hover:bg-button-primary-hover rounded-full px-8 py-3 font-['Open_Sans'] text-base font-medium leading-5 text-white transition-colors"
              onClick={onCancel}
            >
              Close
            </button>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

type ButtonPanelProps = {
  type?: 'submit' | 'button';
  onNextStep?: (event: MouseEvent) => void;
  onCancel?: () => void;
  isLoading?: boolean;
};

export const ButtonPanel: React.FC<ButtonPanelProps> = ({
  onNextStep,
  onCancel,
  type = 'button',
  isLoading = false,
}) => {
  return (
    <div className="border-input-border-gray flex items-center justify-center gap-4 border-t px-6 py-4 md:justify-end">
      <button
        type="button"
        onClick={onCancel || onNextStep}
        className="border-input-border-gray text-label-gray flex h-12 items-center justify-center rounded-full border bg-white px-8 font-['Open_Sans'] text-base font-medium leading-5 transition-colors hover:bg-gray-50"
      >
        {onCancel ? 'Cancel' : 'Skip'}
      </button>
      <button
        type={type}
        onClick={onNextStep}
        disabled={isLoading}
        className="bg-button-primary hover:bg-button-primary-hover flex h-12 items-center justify-center rounded-full px-8 font-['Open_Sans'] text-base font-medium leading-5 text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-300"
      >
        {isLoading ? 'Submitting...' : type === 'submit' ? 'Finish' : 'Next'}
      </button>
    </div>
  );
};

export interface SubheaderProps {
  question: ReactNode;
  postCall?: PostCall;
  className?: string;
}

export const Subheader: React.FC<SubheaderProps> = ({ question, postCall, className }) => (
  <div className={twMerge("flex flex-col gap-2 font-['Albert_Sans']", className)}>
    <p className="text-label-gray text-xl font-medium leading-7">{question}</p>
    {postCall && (
      <p className="text-body-gray text-sm font-normal leading-5">
        {Math.floor(postCall.call.logged_duration / 60)} minute call on{' '}
        {moment(postCall.call.ended_at).format('MMMM DD, h:mm a')}
      </p>
    )}
  </div>
);
